import { ModalFooter } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { Autocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { useNavigate } from "react-router-dom";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useRefresh, useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { TabLayout } from "@hostingbv/lib/src/components/content/TabLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWarning } from "@fortawesome/free-solid-svg-icons";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";


export const AddSshKeyModal: FC<{ defaultKeyType: "Personal key" | "Deployment key" }> = (props) => {
  const { partnerClient } = useApiClient(config)
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [publicKey, setPublicKey] = useState('')
  const [keyType, setKeyType] = useState<string>(props.defaultKeyType)
  const [contact, setContact] = useState('')
  const [error, setError] = useState('')

  const refresh = useRefresh()

  const save = useCallback(async () => {
    const payload = keyType === "Personal key" ? {
      customer_contact: contact,
      public_key: publicKey,
    } : {
      display_name: name,
      public_key: publicKey,
    }
    await partnerClient.createSshKey(payload).then(result => {
      if (!result.success) {
        setError(result.error)
        throw new Error(result.error)
      } else {
        navigate(`/ssh-keys/${result.data.id}`)
        refresh()
      }
    })
  }, [name, publicKey, keyType, contact])

  const contactOptions = useContactOptions()

  const isShortRsaKey = useMemo(() => {
    const match = /^ssh-rsa\s[A-Za-z0-9+/=]+/.exec(publicKey)
    if (match === null) {
      return false
    }
    return match[0].length < 264
  }, [publicKey])
  const isStupid = useMemo(() => {
    return publicKey.includes('PRIVATE KEY-----')
  }, [publicKey])


  return <>
    <form onSubmit={() => save()} className={"space-y-4 flex-1"}>
      <TabLayout tabNames={["Personal key", "Deployment key"]} activeTab={keyType} setActiveTab={setKeyType} />

      {isShortRsaKey && <div className={"flex items-center space-x-3  px-3 py-2 bg-amber-50 rounded-sm"}>
        <FontAwesomeIcon icon={faWarning} className={"text-amber-600"} />
        <span className={"font-medium text-slate-800 text-sm"}>
          You are using a short RSA key (&lt;3072 bit). <br />
          This is considered unsafe. Consider using a 4096 bit RSA key or an Ed25519 key.
        </span>
      </div>}
      {isStupid && <div className={"flex items-center space-x-3  px-3 py-2 bg-amber-50 rounded-sm"}>
        <span>🤦</span>
        <span className={"font-medium text-slate-800 text-sm"}>
          <strong>OH NO! You are using a private key.</strong><br />
          Never, ever, ever, share your private key with anyone including us. Use your public key instead.
        </span>
      </div>}
      <Input label={"Public Key"} type={"textarea"} placeholder={'ssh-ed25519 ...'} value={publicKey} onChange={(v) => setPublicKey(v)} />


      {keyType === "Personal key" ? <>
        <div className={"flex items-center max-w-md space-x-4"}>
          <Autocomplete label={"Contact"} value={contact} options={contactOptions} onChange={(v) => setContact(v)} />
        </div>
      </> : <>
        <div className={"flex items-center max-w-md space-x-4"}>
          <Input label={"Name"} type={"text"} value={name} onChange={(v) => setName(v)} />
        </div>
      </>}
      {error.length > 0 && <div className={"font-medium text-red-600"}>
        {error}
      </div>}
    </form>
    <ModalFooter text={"Save"} onSubmit={save} />
  </>
}

function useContactOptions() {
  const { partnerClient } = useApiClient(config)
  const { resource: customerResource, reload: reloadCustomers } = useFetchedResource(() => partnerClient.getAllCustomers())
  const customers: Customer[] | null = useMemo(() => customerResource?.success === true ? customerResource.data : null, [customerResource])
  useRefreshEffect(() => { reloadCustomers() })
  const [ownCustomerDetails, setOwnCustomerDetails] = useState<Customer | null>(null)
  useEffect(() => {
    const myCustomer: Customer | null = customers?.find(customer => customer.isTenantOwner) ?? null
    if (myCustomer !== null) {
      partnerClient.getCustomer(myCustomer.id).then(customerResponse => {
        if (customerResponse.success) {
          setOwnCustomerDetails(customerResponse.data)
        }
      })
    }
  }, [customers])
  return useMemo(() => {
    if (ownCustomerDetails === null) {
      return {}
    }
    return Object.fromEntries(ownCustomerDetails.contacts.map(contact => [contact.id, `${contact.firstName} ${contact.lastName}`]))
  }, [ownCustomerDetails])
}
